/**
 * Hook para usar el servicio de WebSocket
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { ChatMessage } from '../types';
import webSocketService from '../services/webSocketService';
import configService from '../services/configService';
import conversationSaverService from '../services/conversationSaverService';
import { logger } from '../utils/logger';

export interface UseWebSocketReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isConnecting: boolean;
  sendAudioMessage: (audioBlob: Blob) => Promise<void>;
  sendTextMessage: (text: string) => Promise<void>;
  error: string | null;
  // STT (audio→texto): true mientras se transcribe el audio recién grabado.
  isTranscribing: boolean;
  // Contador que se incrementa cada vez que el STT NO entendió. La UI lo observa
  // para mostrar un aviso transitorio "no te entendí" (usar contador y no boolean
  // permite re-disparar el aviso aunque dos fallos seguidos sean idénticos).
  notRecognizedSignal: number;
}

export function useWebSocket(shouldConnect: boolean = false): UseWebSocketReturn {
  // Cargar mensajes históricos desde webSocketService al iniciar
  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    return webSocketService.getChatHistory();
  });
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [notRecognizedSignal, setNotRecognizedSignal] = useState(0);
  const hasInitializedRef = useRef(false);
  const lastHistoryLengthRef = useRef(0);
  const lastMessageContentRef = useRef<string>('');

  useEffect(() => {
    // Configurar callbacks
    const messageCallback = (_message: ChatMessage) => {
      // El mensaje ya está en chathistory del webSocketService.
      // Actualizamos sin gate: el callback se dispara una vez por evento real
      // (push nuevo o mutación in-place de la última burbuja). El gate previo
      // comparaba length + last.content, pero en barge-in podían colarse
      // updates que coincidían en ambas dimensiones (ej. push de user-msg
      // intercalado con chunks late del turno anterior que mutaban a la misma
      // length+content). Resultado: la burbuja del usuario no aparecía tras
      // cortar a Galia y hablar. Santiago 2026-05-26 #4.
      const updatedHistory = webSocketService.getChatHistory();
      lastHistoryLengthRef.current = updatedHistory.length;
      lastMessageContentRef.current = updatedHistory.length > 0
        ? updatedHistory[updatedHistory.length - 1].content
        : '';
      setMessages([...updatedHistory]);
    };

    webSocketService.onMessage(messageCallback);

    // Callback para cuando se limpia el historial
    const historyClearedCallback = () => {
      const updatedHistory = webSocketService.getChatHistory();
      lastHistoryLengthRef.current = updatedHistory.length;
      lastMessageContentRef.current = updatedHistory.length > 0 ? updatedHistory[updatedHistory.length - 1].content : '';
      setMessages([...updatedHistory]);
    };

    webSocketService.onChatHistoryCleared(historyClearedCallback);

    // Estado del STT: indicador "Transcribiendo…" y aviso de "no te entendí".
    const transcriptionStateCallback = (state: 'transcribing' | 'done' | 'not_recognized') => {
      if (state === 'transcribing') {
        setIsTranscribing(true);
      } else if (state === 'done') {
        setIsTranscribing(false);
      } else {
        // not_recognized: cerrar indicador y disparar el aviso transitorio
        setIsTranscribing(false);
        setNotRecognizedSignal((n) => n + 1);
      }
    };

    webSocketService.onTranscriptionState(transcriptionStateCallback);

    // El audio ahora se maneja internamente en webSocketService con cola
    // El callback onAudio se usa solo para notificaciones, no para reproducir directamente
    const audioCallback = (_audioUrl: string) => {
      // El audio ya se está reproduciendo en webSocketService.tryPlayNextAudio()
      // Este callback se mantiene por compatibilidad pero no hace nada aquí
    };

    webSocketService.onAudio(audioCallback);

    // Inicializar el ref con la longitud actual
    lastHistoryLengthRef.current = webSocketService.getChatHistory().length;

    // Cleanup: desregistrar callbacks cuando el componente se desmonte
    return () => {
      webSocketService.offMessage();
      webSocketService.offAudio();
      webSocketService.offChatHistoryCleared();
      webSocketService.offTranscriptionState();
    };
  }, []);

  useEffect(() => {
    // Solo intentar conectar si shouldConnect es true
    if (!shouldConnect) {
      // Resetear el flag si shouldConnect cambia a false
      hasInitializedRef.current = false;
      setIsConnecting(false);
      setIsConnected(false);
      return;
    }

    // Al entrar en modo conexión, sincronizar mensajes desde chathistory para no perder historial al abrir el chat
    const currentHistory = webSocketService.getChatHistory();
    lastHistoryLengthRef.current = currentHistory.length;
    lastMessageContentRef.current = currentHistory.length > 0 ? currentHistory[currentHistory.length - 1].content : '';
    setMessages([...currentHistory]);

    // Evitar múltiples inicializaciones
    if (hasInitializedRef.current) {
      return;
    }
    hasInitializedRef.current = true;
    let isMounted = true;
    
    // Establecer estado de conexión
    setIsConnecting(true);
    setIsConnected(false);
    
    // Registrar callback para cuando la conexión esté completamente lista
    const handleConnectionReady = () => {
      if (isMounted) {
        setIsConnecting(false);
        setIsConnected(true);
        setError(null);
        // El mensaje inicial se envía automáticamente desde webSocketService.ts cuando la conexión está lista
      }
    };
    
    webSocketService.onConnectionReady(handleConnectionReady);
    
    // Inicializar conexión
    logger.log('[WS-DIAG] useWebSocket → llamando initialize()');
    webSocketService
      .initialize()
      .then(() => {
        logger.log('[WS-DIAG] initialize() resuelto OK');
      })
      .catch((err) => {
        console.error('[WS-DIAG] initialize() rechazado:', err?.message || err);
        hasInitializedRef.current = false; // Permitir reintento en caso de error
        if (isMounted) {
          console.warn('Error al conectar WebSocket (modo desarrollo):', err);
          // En modo desarrollo/testing, simular conexión exitosa después de un delay
          const config = configService.getConfig();
          if (config.testingPhase || config.testingWebSocket) {
            setTimeout(() => {
              if (isMounted) {
                setIsConnected(true);
                setError(null);
              }
            }, 2000);
          } else {
            setError(err.message || 'Error al conectar WebSocket');
            setIsConnected(false);
          }
        }
      });

    return () => {
      isMounted = false;
      webSocketService.offConnectionReady();
      // Solo desconectar si shouldConnect cambia a false
      if (!shouldConnect) {
        webSocketService.disconnect();
        hasInitializedRef.current = false;
        setIsConnecting(false);
        setIsConnected(false);
      }
    };
  }, [shouldConnect]);

  const sendAudioMessage = useCallback(async (audioBlob: Blob) => {
    try {
      // Incrementar contador de audio
      conversationSaverService.incrementAudioCount();
      
      await webSocketService.sendAudioMessage(audioBlob);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error al enviar mensaje de audio');
    }
  }, []);

  const sendTextMessage = useCallback(async (text: string) => {try {
      // Incrementar contador de texto
      conversationSaverService.incrementTextCount();await webSocketService.sendTextMessage(text);
      // No actualizar manualmente aquí, el callback onMessage ya lo hace
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Error al enviar mensaje de texto');
    }
  }, []);

  return {
    messages,
    isConnected,
    isConnecting,
    sendAudioMessage,
    sendTextMessage,
    error,
    isTranscribing,
    notRecognizedSignal,
  };
}


